import { useNavigate, useParams } from 'react-router-dom';
import { useBodyZoneListQuery, useExerciseQuery } from '@/hooks/query-hooks';
import { ImageDialog } from '@/components/ImageDialog';
import { ImageFromServer } from '@/components/images/ImageFromServer';
import { VideoFromServer } from '@/components/video/VideoFromServer';
import { ExerciseDTO } from '@/generated';
// import { useThesaurusTranslation } from '@/hooks/use-thesaurus-translation.hook';
import { safeSchema } from '@/utils/rehype';
import { Modal, Box, Grow, Paper, Grid, Typography, Button, Stack, Link, Tooltip, IconButton } from '@mui/material';
import { useContext, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { ExerciseEquipmentList } from '../equipment/ExerciseEquipmentList';
import { MuscleTagIntensityList } from './elements/TagIntensityList';
import { DefaultContainer } from '@/components/DefaultContainer';
import { ArrowBack } from '@mui/icons-material';
import { ExercisePrerequisiteList, ExerciseProRegressionList } from './elements/ExerciseProgressionList';
import { ExerciseFactsList } from './elements/ExerciseFactsList';
import { exerciseDetailsPaperListStyle } from '@/styles';
import { ExerciseInfoList } from './elements/ExerciseInfoList';
import { AdditionalImagesComponent } from './elements/AdditionalImagesComponent';
import { PaperSubheader } from './elements/PaperSubheader';
import { CopyExerciseLinkButton } from '@/components/icon-buttons/CopyExerciseLinkButton';
import { EditExerciseButton } from '@/components/icon-buttons/EditExerciseButton';
import { DeleteExerciseButton } from '@/components/icon-buttons/DeleteExerciseButton';
import { AppContext } from '@/contexts/app.context';
import { isExerciseEditor } from './exercise-utils';
import { DeleteModal } from '@/components/DeleteModal';
import { useDeleteExercise } from '@/hooks/exercise-mutation.hooks';
import { enqueueSnackbar } from 'notistack';
import { pathMap } from '@/navigation/paths';

export const ExerciseDetailsPage = () => {
  const { exerciseId } = useParams();
  const { exercise } = useExerciseQuery(exerciseId);

  return exercise ? <ExerciseDetails exercise={exercise} /> : <></>;
};

interface ExerciseProps {
  exercise: ExerciseDTO;
}

export const ExerciseDetails = (props: ExerciseProps) => {
  const { exercise } = props;
  const { state } = useContext(AppContext);
  const user = state?.userProfile;
  const { bodyZoneList } = useBodyZoneListQuery();
  const [textModalMode, setTextModalMode] = useState<'desc' | 'link' | undefined>();
  const [showVideo, setShowVideo] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalImageId, setModalImageId] = useState<string>('');
  const navigate = useNavigate();
  const deleteExerciseMutation = useDeleteExercise();

  return (
    <DefaultContainer>
      <DeleteModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        nameOfObjectToDelete="Exercise"
        handleDelete={async () => {
          await deleteExerciseMutation.mutateAsync(exercise.id);
          enqueueSnackbar(`Exercise ${exercise.name} deleted!`, { variant: 'success' });
          setShowDeleteModal(false);
          navigate(pathMap.Exercises);
        }}
      />
      <Paper sx={{ my: 1, zIndex: 1, padding: 1, position: 'sticky', top: { xs: 60, sm: 65 } }}>
        <Grid container gap={2} direction={'row'} sx={{ alignContent: 'center', alignItems: 'center' }}>
          <Tooltip title={'Back to all Exercises'}>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBack />
            </IconButton>
          </Tooltip>
          <CopyExerciseLinkButton exerciseId={exercise.id} />
          {user && isExerciseEditor(exercise, user) && <EditExerciseButton exerciseId={exercise.id} />}
          {user && isExerciseEditor(exercise, user) && <DeleteExerciseButton setShowDeleteModal={setShowDeleteModal} />}
        </Grid>
      </Paper>
      <Modal open={textModalMode !== undefined} onClose={() => setTextModalMode(undefined)} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90vw',
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4,
          }}
        >
          {textModalMode === 'link' && <ExerciseLinks exercise={props.exercise} />}
          {textModalMode === 'desc' && <ExerciseDescription exercise={props.exercise} />}
        </Box>
      </Modal>
      <ImageDialog
        imageId={modalImageId}
        open={modalImageId !== ''}
        onClose={() => {
          setModalImageId('');
        }}
      />
      <Grow timeout={100} in={true}>
        <Paper style={{ width: '100%' }} elevation={4}>
          <Grid sx={{ justifyContent: 'center' }} container>
            <Grid size={12} sx={{ pl: 5, py: 2, overflowWrap: 'break-word' }}>
              <Stack gap={1} direction={'row'} sx={{ alignItems: 'flex-end' }}>
                <Typography variant="h4">{props.exercise.name}</Typography>
                {exercise.synonyms?.length ? (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      also known as: {exercise.synonyms.join(', ')}
                    </Typography>
                  </>
                ) : (
                  <></>
                )}
              </Stack>
              <Typography sx={{}} variant="h6">
                {bodyZoneList?.find((bz) => bz.id === props.exercise.mainBodyZoneId)?.name ?? 'No Zone'}
                {props.exercise.isUniLateral ? ', Unilateral' : ''}
              </Typography>
            </Grid>
            <Grid size={12} container spacing={2} sx={{ px: 2 }} alignItems="stretch">
              <Grid size={{ md: 4, xs: 12 }}>
                <Paper elevation={8} sx={{ p: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Box sx={{ width: '100%', aspectRatio: '1', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {showVideo && <VideoFromServer sx={{ width: '100%', height: '100%' }} id={props.exercise.videoId} />}
                    {!showVideo && <ImageFromServer sx={{ width: '100%', height: '100%', objectFit: 'contain' }} thumbnail={true} alt="" id={props.exercise.imageId} />}
                  </Box>
                  {props.exercise.videoId && <Button onClick={() => setShowVideo(!showVideo)}>Toggle Video</Button>}
                </Paper>
              </Grid>
              <Grid
                size={{ md: 8, xs: 12 }}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  minWidth: 0,
                  minHeight: 0,
                  aspectRatio: { xs: '1', md: '2 ' },
                  overflow: 'hidden',
                }}
              >
                <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', textOverflow: 'ellipsis' }} elevation={8} onClick={(e) => e.preventDefault()}>
                  <PaperSubheader title="Description" />

                  <Box sx={{ flex: 1, minHeight: 0 }} onClick={() => setTextModalMode('desc')}>
                    <ExerciseDescription exercise={props.exercise} />
                  </Box>
                </Paper>
                {props.exercise.links.length > 0 && (
                  <Paper sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }} elevation={8}>
                    <PaperSubheader title="Links" />
                    <Box onClick={() => setTextModalMode('link')}>
                      <ExerciseLinks exercise={props.exercise} />
                    </Box>
                  </Paper>
                )}
              </Grid>
            </Grid>
            <Grid
              size={12}
              container
              spacing={2}
              sx={{
                p: 2,
                '& > .MuiGrid-root': { display: 'flex', minWidth: 0 },
                '& > .MuiGrid-root > *': { flex: 1, minWidth: 0 },
              }}
            >
              <Grid size={12}>
                <ExerciseFactsList exercise={exercise} />
              </Grid>
              {!!exercise.additionalImageIds?.length && (
                <Grid size={12}>
                  <AdditionalImagesComponent exercise={exercise} setModalImageId={setModalImageId} />
                </Grid>
              )}
              <Grid size={{ md: 3, xs: 12 }}>
                <ExerciseInfoList exercise={exercise} />
              </Grid>
              <Grid size={{ md: 3, xs: 12 }}>
                <ExerciseEquipmentList equipment={props.exercise.equipment} />
              </Grid>
              <Grid size={{ md: 6, xs: 12 }}>
                <ExercisePrerequisiteList prerequisites={props.exercise.prerequisites} />
              </Grid>
              <Grid size={{ md: 4, xs: 12 }}>
                <ExerciseProRegressionList title="Progressions" ids={props.exercise.progressionExerciseIds} showExerciseTooltip />
              </Grid>
              <Grid size={{ md: 4, xs: 12 }}>
                <ExerciseProRegressionList title="Regressions" ids={props.exercise.regressionExerciseIds} showExerciseTooltip />
              </Grid>
              <Grid size={{ md: 4, xs: 12 }}>
                <ExerciseProRegressionList
                  title="Support exercises"
                  ids={props.exercise.supportExercises.map((supportExercise) => supportExercise.exerciseId)}
                  descriptions={Object.fromEntries(props.exercise.supportExercises.map((supportExercise) => [supportExercise.exerciseId, supportExercise.description]))}
                  showExerciseTooltip
                />
              </Grid>
              <Grid size={12}>
                <Paper sx={exerciseDetailsPaperListStyle} elevation={8}>
                  <MuscleTagIntensityList muscleIntensities={props.exercise.muscleIntensities} />
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </Grow>
    </DefaultContainer>
  );
};

const ExerciseDescription = (props: { exercise: ExerciseDTO }) => {
  // const { translate } = useThesaurusTranslation();
  return (
    <Box
      sx={{
        height: '100%',
        p: 1,
        background: (theme) => `linear-gradient(to bottom, transparent, ${theme.palette.background.paper})`,
        overflow: 'hidden', // allows scroll if content is bigger
        WebkitMaskImage: (theme) => `linear-gradient(to bottom, ${theme.palette.background.paper} 80%, transparent 100%)`,
      }}
    >
      <Box
        sx={{
          overflowY: 'scroll',
          boxSizing: 'content-box',
          width: '100%',
          height: '100%',
          pr: 15,
        }}
      >
        <ReactMarkdown rehypePlugins={[rehypeRaw, [rehypeSanitize, safeSchema]]} remarkPlugins={[remarkGfm]}>
          {props.exercise.description !== '' ? props.exercise.description : '-'}
        </ReactMarkdown>
      </Box>
    </Box>
  );
};

const ExerciseLinks = (props: { exercise: ExerciseDTO }) => {
  return (
    <Stack sx={{ pl: 1.5, pt: 0.5 }} direction={'column'} alignItems={'flex-start'} alignContent={'center'}>
      {props.exercise.links.map((el, i) => (
        <>
          <Link sx={{ pb: 1 }} underline="none" onClick={(e) => e.stopPropagation()} href={el.uri}>
            - {el.title}
          </Link>
        </>
      ))}
    </Stack>
  );
};
